<template>
  <div v-if="loaded" class="code-editor-container" ref="container"></div>
</template>

<script>
export default {
  name: 'CodeEditor',
  props: {
    value: {
      type: String,
      default: ''
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loaded: true,
      monaco: null,
      instance: null,
      // 代码标记关键字
      markWord: null
    }
  },
  methods: {
    // 重新调整大小
    resize () {
      this.loaded = false
      this.$nextTick(() => {
        this.loaded = true
        this.$nextTick(() => {
          this.init()
          this.markWords(this.markWord)
        })
      })
    },
    // 初始化
    init () {
      // 引入freemarker配置
      require('./freemarker/freemarker.contribution')
      // 引入monaco
      this.monaco = require('monaco-editor/esm/vs/editor/editor.main.js')
      // 初始化自动补全
      this.__initCompletion()
      // 初始化主题
      this.__initTheme()
      // 初始化编辑器
      this.instance = this.monaco.editor.create(this.$refs.container, {
        value: this.value,
        theme: 'coderd-dark',
        language: 'freemarker',
        readOnly: this.readOnly,
        fontSize: '13px',
        // automaticLayout: true, // 允许编辑器安装一个间隔来检查其容器dom节点大小是否已更改。启用此功能可能会对性能产生严重影响。默认值为false。
        scrollBeyondLastLine: false, // 是否允许滚动到仅留下最后一行记录，默认为true
        features: ['!unusualLineTerminators'], // 换行符号处理，避免mac换行和windows换行符的差异（未验证）
        scrollbar: {
          verticalScrollbarSize: 12, // 垂直滚动条大小
          horizontalScrollbarSize: 12 // 水平滚动条大小
        }
      })
      // 初始化快捷键
      this.__initShortcuts()
      // 绑定数据变更
      this.instance.getModel().onDidChangeContent(() => {
        this.change()
      })
      this.monaco.inited = true
    },
    change () {
      this.$emit('input', this.instance.getModel().getValue())
      this.$emit('change')
      this.markWords(this.markWord)
    },
    // 标记内容
    markWords (word) {
      this.markWord = word
      // 清空标记
      const decorations = this.instance.getModel().getAllDecorations()
      this.instance.getModel().deltaDecorations(decorations.map(item => item.id), [])
      if (word == null || word.length === 0) {
        return
      }
      // 添加标记
      const positions = []
      // - 查找目标文字索引
      const lines = this.instance.getModel().getValue().split('\n')
      for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        const line = lines[lineIndex]
        const tokens = line.toLowerCase().split(word.toLowerCase())
        if (tokens.length === 1) {
          continue
        }
        for (let tokenIndex = 0; tokenIndex < tokens.length; tokenIndex++) {
          if (tokenIndex === 0) {
            continue
          }
          const startColumn = tokens.slice(0, tokenIndex).join(word).length + 1 // 列数从1开始计数
          positions.push({
            lineNumber: lineIndex + 1, // 行数从1开始计数
            startColumn,
            endColumn: startColumn + word.length
          })
        }
      }
      // - 注册标记
      this.instance.getModel().deltaDecorations([], [
        ...positions.map(position => {
          return {
            range: new this.monaco.Range(position.lineNumber, position.startColumn, position.lineNumber, position.endColumn),
            options: {
              isWholeLine: false,
              className: 'mark-word'
            }
          }
        })
      ])
    },
    // 初始化自动补全
    __initCompletion () {
      if (this.monaco.inited) {
        return
      }
      const keywords = require('./freemarker/keywords')
      this.monaco.languages.registerCompletionItemProvider('freemarker', {
        provideCompletionItems: () => {
          // 此处必须拷贝一份keywords，否则仅会提醒一次（八成是引用问题，但不清除为何此处会存在引用问题）
          return { suggestions: JSON.parse(JSON.stringify(keywords)) }
        }
      })
    },
    // 初始化主题
    __initTheme () {
      if (this.monaco.inited) {
        return
      }
      this.monaco.editor.defineTheme('coderd-dark', {
        // 继承的主题
        base: 'vs-dark',
        inherit: true,
        rules: [
          // 配置高亮色，这里的token与语言定义中的tokenizer token一致
          // 默认颜色
          { token: '', foreground: '#b4b4b4' },
          // 标签色
          { token: 'tag', foreground: '#cc7832' },
          // 变量
          { token: 'variable', foreground: '#e2bb68' },
          // 函数
          { token: 'function.start', foreground: '#5a7d99' },
          { token: 'function.name', foreground: '#6897bb' },
          // 字符串
          { token: 'string', foreground: '#6a8759' },
          // 属性参数
          { token: 'attribute.name', foreground: '#b4b4b4' },
          { token: 'attribute.value', foreground: '#6a8759' },
          // !注释标签样式
          { token: 'hcomment', foreground: '#808080' },
          // !注释标签内容样式
          { token: 'hcomment.content', foreground: '#808080' },
          { token: 'hcomment.content.invalid', foreground: '#808080' },
          // noparse内容
          { token: 'noparse.content', foreground: '#a4a4a4' },
          // 右侧缩略背景色
          { background: '#272e31' }
        ],
        colors: {
          // 编辑器背景色
          'editor.background': '#1d2627',
          // 聚焦行的边框色
          'editor.lineHighlightBorder': '#3b4345'
        }
      })
    },
    // 初始化快捷键
    __initShortcuts () {
      const _this = this
      this.instance.addAction({
        id: 'save',
        label: 'save',
        keybindings: [
          this.monaco.KeyMod.CtrlCmd | this.monaco.KeyCode.KEY_S
        ],
        run: function () {
          _this.$emit('save', _this.instance.getModel().getValue())
        }
      })
    }
  },
  mounted () {
    this.init()
  }
}
</script>

<style scoped lang="scss">
@import "../../variables";
.code-editor-container {
  width: 100%;
  height: 100%;
  /deep/ .monaco-editor {
    // 搜索框
    .find-widget {
      background: $color-gray-deep;
      box-shadow: 1px 1px 20px -4px #111;
    }
    .suggest-widget.docs-side > .tree, .monaco-editor .suggest-widget.docs-side > .details {
      max-width: 400px;
      background: $color-gray-deep;
      // 关键字提示文档
      .docs {
        font-size: 12px;
      }
    }
  }
  /deep/ .mark-word {
    background: $primary-color;
  }
}
</style>
